import TemplatePreviewModal from './TemplatePreviewModal';
import ClientDocumentProfileEditor from '../components/ClientDocumentProfileEditor';
import CompanyLookup from '../components/CompanyLookup';
import { sellerAsCompany, applySellerCompany } from '../shared/companyLookup';
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import PeopleRounded from "@mui/icons-material/PeopleRounded";
import QueueRounded from "@mui/icons-material/QueueRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import { Workspace, WorkspaceHeader } from "../components/Workspace/Workspace";
import { LayoutContext } from "../context";
import { positionsOf } from "../roles";
import { documentRequest, downloadTemplateFile } from "./documentSettingsApi";
import {
  CLIENT_TYPES,
  DOC_TYPES,
  Choice,
  CustomerEditor,
  Field,
  SearchPicker,
  Section,
  nameOf,
} from "../content/Manager/Documents/DocumentUI";

const TAB_KEYS = ['automation', 'diadoc', 'seller', 'templates', 'client', 'queue'];
const initialTab = () => Math.max(0, TAB_KEYS.indexOf(new URLSearchParams(window.location.search).get('tab')));

const typeName = (v) => DOC_TYPES.find(([k]) => k === v)?.[1] || v;
const states = {
  pending: "Ожидает",
  running: "Выполняется",
  blocked: "Ожидает условий",
  retry: "Повторная попытка",
  error: "Ошибка",
  done: "Выполнено",
  cancelled: "Отменено",
};
const kinds = {
  contract: "Договор",
  invoice: "Счёт",
  closing: "Закрывающий документ",
  edo: "Передача в Диадок",
};
const tabs = [
  ["Автоматика", AutoAwesomeRounded],
  ["Диадок", LinkRounded],
  ["Наши реквизиты", BusinessRounded],
  ["Шаблоны", DescriptionRounded],
  ["Клиенты", PeopleRounded],
  ["Очередь", QueueRounded],
];
function Toggle({ title, description, checked, onChange }) {
  return (
    <div className="doc-toggle">
      <Box>
        <Typography level="title-sm">{title}</Typography>
        {description && (
          <Typography level="body-sm" sx={{ mt: 0.5, color: "text.tertiary" }}>
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        aria-label={title}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ mt: 0.3 }}
      />
    </div>
  );
}
function Secret({ label, saved, clear, value, onChange, onClear }) {
  const [show, setShow] = useState(false);
  return (
    <Stack gap={0.5}>
      <Field
        label={label}
        value={value || ""}
        type={show ? "text" : "password"}
        autoComplete="new-password"
        placeholder={
          clear
            ? "Будет удалён"
            : saved
              ? "Значение сохранено"
              : "Введите значение"
        }
        onChange={onChange}
        endDecorator={
          <IconButton
            size="sm"
            aria-label={show ? "Скрыть секрет" : "Показать секрет"}
            onClick={() => setShow((v) => !v)}
          >
            {show ? <VisibilityOffRounded /> : <VisibilityRounded />}
          </IconButton>
        }
      />
      <div className="doc-secret-action">
        <Typography
          level="body-xs"
          color={clear ? "danger" : saved ? "success" : "neutral"}
        >
          {clear
            ? "Удаление после сохранения"
            : saved
              ? "Сохранено на сервере"
              : "Не сохранено"}
        </Typography>
        {(saved || clear) && (
          <Button
            size="sm"
            variant="plain"
            color={clear ? "neutral" : "danger"}
            onClick={onClear}
          >
            {clear ? "Отменить удаление" : "Удалить"}
          </Button>
        )}
      </div>
    </Stack>
  );
}
export default function DocumentSettingsPage() {
  const { tokenEmployee, account } = useContext(LayoutContext),
    reduce = useReducedMotion();
  const admin =
    account?.role === "administrator" ||
    positionsOf(account).includes("head_manager");
  const [tab, setTab] = useState(initialTab),
    [conf, setConf] = useState(null),
    [seller, setSeller] = useState({}),
    [templates, setTemplates] = useState([]),
    [tasks, setTasks] = useState([]),
    [counters, setCounters] = useState([]);
  const [secrets, setSecrets] = useState({}),
    [boxes, setBoxes] = useState([]),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState(null),
    [clientEdit, setClientEdit] = useState(false),
    [tplType, setTplType] = useState(""),
    [taskState, setTaskState] = useState("");
  const [counter, setCounter] = useState({
    doc_type: "invoice",
    year: new Date().getFullYear(),
    last_number: 0,
  });
  const profileSeq = useRef(0);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const pageScopeRef = useRef(null);
  if (pageScopeRef.current?.token !== tokenEmployee || pageScopeRef.current?.accountId !== account?.id) {
    pageScopeRef.current = { token: tokenEmployee, accountId: account?.id, active: true };
  }
  const pageScope = pageScopeRef.current;
  useEffect(() => {
    pageScope.active = true; setPreviewTemplate(null); setCustomer(null); setConf(null);
    return () => { pageScope.active = false; };
  }, [pageScope]);
  const request = async (path, options = {}) => {
    const result = await documentRequest(path, { token: tokenEmployee, ...options });
    if (!pageScope.active || pageScopeRef.current !== pageScope) throw new Error('Запрос относится к прежнему аккаунту.');
    return result;
  };
  const act = async (fn) => {
    const scope = pageScopeRef.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      if (scope.active && pageScopeRef.current === scope) setError(e.message);
    } finally {
      if (scope.active && pageScopeRef.current === scope) setBusy(false);
    }
  };
  const load = async () => {
    const [c, s, t, q, n] = await Promise.all([
      request("configuration/"),
      request("sellers/"),
      request("templates/"),
      request("tasks/"),
      request("counters/"),
    ]);
    setConf(c);
    setSeller(
      s.find((v) => v.is_default) || s[0] || { name: "", legal_form: "ip" },
    );
    setTemplates(t);
    setTasks(q);
    setCounters(n);
  };
  useEffect(() => {
    if (tokenEmployee && admin) act(load);
  }, [tokenEmployee, admin, pageScope]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (k, v) => setConf((c) => ({ ...c, [k]: v }));
  const selectTab = (index) => {
    setTab(index);
    setError("");
    setNotice("");
  };
  const tabKeys = (event) => {
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : event.key === "ArrowRight"
            ? (tab + 1) % tabs.length
            : event.key === "ArrowLeft"
              ? (tab + tabs.length - 1) % tabs.length
              : null;
    if (next === null) return;
    event.preventDefault();
    selectTab(next);
    event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus();
  };
  const save = () =>
    act(async () => {
      setConf(
        await request("configuration/", {
          method: "PATCH",
          data: { ...conf, ...secrets },
        }),
      );
      setSecrets({});
      setNotice("Настройки сохранены");
    });
  const openClient = async (client) => {
    const sequence = ++profileSeq.current;
    setCustomer(null); setClientEdit(false); setError(''); setNotice(''); setBusy(true);
    try {
      const full = await request(`customers/${client.id}/`);
      if (sequence === profileSeq.current) setCustomer(full);
    } catch (failure) {
      if (pageScope.active && pageScopeRef.current === pageScope && sequence === profileSeq.current) setError(failure.message);
    } finally {
      if (pageScope.active && pageScopeRef.current === pageScope && sequence === profileSeq.current) setBusy(false);
    }
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('client_id');
    if (!tokenEmployee || !admin || !/^[1-9][0-9]*$/.test(id || '')) return;
    let cancelled = false;
    request(`customers/${id}/`).then(client => {
      if (!cancelled) { setCustomer(client); setClientEdit(false); setTab(4); }
    }).catch(failure => { if (!cancelled) setError(failure.message); });
    return () => { cancelled = true; };
  }, [tokenEmployee, admin, pageScope]);
  const saveButton = (label) => (
    <Button loading={busy} onClick={save} startDecorator={<SaveRounded />}>
      {label || "Сохранить настройки"}
    </Button>
  );
  const secretField = (k, label) => (
    <Secret
      key={k}
      label={label}
      saved={conf[`${k}_saved`]}
      clear={secrets[`clear_${k}`]}
      value={secrets[k]}
      onChange={(v) =>
        setSecrets((s) => ({ ...s, [k]: v, [`clear_${k}`]: false }))
      }
      onClear={() =>
        setSecrets((s) => ({ ...s, [k]: "", [`clear_${k}`]: !s[`clear_${k}`] }))
      }
    />
  );
  if (!admin)
    return (
      <Workspace className="doc-workspace">
        <Alert color="warning">
          Настройки доступны руководителю и администратору.
        </Alert>
      </Workspace>
    );
  return (
    <Workspace className="doc-workspace">
      <WorkspaceHeader
        title="Настройки документов"
        subtitle="Автоматика, реквизиты, шаблоны и электронный документооборот."
      >
        <Button
          component={Link}
          to="/documents"
          variant="soft"
          startDecorator={<ArrowBackRounded />}
        >
          К документам
        </Button>
      </WorkspaceHeader>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {!conf ? (
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress />
          {error && (
            <Button onClick={() => act(load)} sx={{ mt: 2 }}>
              Повторить загрузку
            </Button>
          )}
        </Stack>
      ) : (
        <>
          <nav
            className="doc-settings-tabs"
            role="tablist"
            aria-label="Настройки документов"
            onKeyDown={tabKeys}
          >
            {tabs.map(([label, Icon], i) => (
              <Button
                key={label}
                role="tab"
                id={`doc-tab-${i}`}
                aria-controls="doc-settings-panel"
                aria-selected={tab === i}
                tabIndex={tab === i ? 0 : -1}
                variant={tab === i ? "solid" : "soft"}
                color={tab === i ? "primary" : "neutral"}
                startDecorator={<Icon sx={{ fontSize: 19 }} />}
                onClick={() => selectTab(i)}
              >
                {label}
                {i === 5 && tasks.some((t) => t.state === "error") && (
                  <Chip size="sm" color="danger">
                    {tasks.filter((t) => t.state === "error").length}
                  </Chip>
                )}
              </Button>
            ))}
          </nav>
          <Box
            component={motion.fieldset}
            disabled={busy}
            sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}
            role="tabpanel"
            id="doc-settings-panel"
            aria-labelledby={`doc-tab-${tab}`}
            key={tab}
            initial={{ opacity: 0, y: reduce ? 0 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.18 }}
          >
            {tab === 0 && (
              <div className="doc-settings-columns">
                <Stack gap={2}>
                  <Section
                    title="Формирование на этапах"
                    description="Система создаёт документы по событиям и использует действующий договор повторно."
                  >
                    <Toggle
                      title="Автоматически формировать и выдавать документы"
                      checked={conf.automation_enabled}
                      onChange={(v) => update("automation_enabled", v)}
                      description="Обрабатываются новые события и следующие переходы стадий. Массового запуска старых заказов нет."
                    />
                    {(conf.stages || []).map((s, i) => (
                      <Card
                        key={s.stage}
                        variant="soft"
                        sx={{ borderRadius: "12px" }}
                      >
                        <Typography level="title-sm">
                          {i + 1}. {s.stage}
                        </Typography>
                        <Typography level="body-sm">{s.documents}</Typography>
                      </Card>
                    ))}
                    <Choice
                      label="Канал выдачи по умолчанию"
                      value={conf.default_channel}
                      onChange={(v) => update("default_channel", v)}
                      options={[
                        ["lk", "Кабинет клиента"],
                        ["email", "Кабинет и письмо с PDF"],
                      ]}
                    />
                    <Alert color="neutral">
                      Дозамер и возврат из КК не создают новый комплект. При
                      ошибке документ остаётся в очереди.
                    </Alert>
                    <Stack direction="row">
                      {saveButton("Сохранить автоматику")}
                    </Stack>
                  </Section>
                </Stack>
                <Section
                  title="Нумерация"
                  description="Отдельная последовательность для каждого типа и года. Уменьшать счётчик нельзя."
                >
                  <Choice
                    label="Тип документа"
                    value={counter.doc_type}
                    onChange={(v) => setCounter((c) => ({ ...c, doc_type: v }))}
                    options={DOC_TYPES}
                  />
                  <div className="doc-grid">
                    <Field
                      label="Год"
                      type="number"
                      value={counter.year}
                      onChange={(v) => setCounter((c) => ({ ...c, year: v }))}
                    />
                    <Field
                      label="Последний номер"
                      type="number"
                      value={counter.last_number}
                      onChange={(v) =>
                        setCounter((c) => ({ ...c, last_number: v }))
                      }
                    />
                  </div>
                  <Button
                    variant="soft"
                    disabled={busy}
                    onClick={() =>
                      act(async () => {
                        setCounters(
                          await request("counters/", {
                            method: "PATCH",
                            data: counter,
                          }),
                        );
                        setNotice("Нумерация сохранена");
                      })
                    }
                  >
                    Установить счётчик
                  </Button>
                  {counters.map((c) => (
                    <div
                      className="doc-summary-line"
                      key={`${c.doc_type}-${c.year}`}
                    >
                      <span>
                        {typeName(c.doc_type)} · {c.year}
                      </span>
                      <strong>{c.last_number}</strong>
                    </div>
                  ))}
                </Section>
              </div>
            )}
            {tab === 1 && (
              <Stack gap={2}>
                <Section
                  title="Подключение к Диадоку"
                  description="Сайт создаёт неподписанные черновики. Подписание и отправка выполняются руководителем в Диадоке."
                >
                  <Toggle
                    title="Включить интеграцию с Диадоком"
                    checked={conf.diadoc_enabled}
                    onChange={(v) => update("diadoc_enabled", v)}
                    description={
                      conf.diadoc_enabled
                        ? "Очередь может передавать документы в Диадок."
                        : "Передача отключена. Проверка доступа доступна."
                    }
                  />
                  <div className="doc-grid">
                    <Choice
                      label="Способ авторизации"
                      value={conf.auth_mode}
                      onChange={(v) => update("auth_mode", v)}
                      options={[
                        ["bearer", "OAuth — автоматическое обновление"],
                        ["legacy", "DiadocAuth — старое подключение"],
                      ]}
                    />
                    <Toggle
                      title="Тестовая площадка"
                      description={
                        conf.diadoc_test
                          ? "Тестовый контур"
                          : "Рабочая площадка"
                      }
                      checked={conf.diadoc_test}
                      onChange={(v) => update("diadoc_test", v)}
                    />
                  </div>
                </Section>
                {conf.diadoc_enabled && conf.diadoc_readiness && <Alert color={conf.diadoc_readiness.local_config_ready ? 'success' : 'warning'}>
                  <Box>
                    <Typography level="title-sm">{conf.diadoc_readiness.local_config_ready ? 'Данные подключения заполнены' : 'Для подключения осталось заполнить'}</Typography>
                    {conf.diadoc_readiness.local_config_ready ? <Typography level="body-sm">Проверьте сохранённый доступ к ящику перед передачей документов.</Typography>
                      : <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>{(conf.diadoc_readiness.missing || []).map((message, index) => <li key={index}>{message}</li>)}</Box>}
                  </Box>
                </Alert>}
                <div className="doc-settings-columns">
                  <Section
                    title="Данные авторизации"
                    description="Сохранённые секреты не отображаются. Пустое поле сохраняет прежнее значение."
                  >
                    <div className="doc-grid">
                      {conf.auth_mode === "bearer" ? (
                        <>
                          <Field
                            label="Client ID приложения"
                            value={conf.oauth_client_id}
                            onChange={(v) => update("oauth_client_id", v)}
                          />
                          {secretField(
                            "client_secret",
                            "API-ключ / client_secret",
                          )}
                          {secretField("refresh_token", "Refresh-токен")}
                          {secretField("token", "Access-токен — необязательно")}
                        </>
                      ) : (
                        <>
                          {secretField("api_key", "Ключ приложения API")}
                          {secretField("token", "Пользовательский токен")}
                        </>
                      )}
                    </div>
                    {conf.auth_mode === "bearer" && (
                      <Alert color="neutral">
                        Для первого подключения достаточно Client ID, API-ключа
                        и refresh-токена. Access-токен сервер получает
                        автоматически.
                      </Alert>
                    )}
                  </Section>
                  <Section
                    title="Ящик и формат УПД"
                    description="Идентификатор ящика и идентификатор участника ЭДО — разные значения."
                  >
                    <Field
                      label="GUID ящика отправителя"
                      value={conf.box_id}
                      onChange={(v) => update("box_id", v)}
                    />
                    <Field
                      label="Идентификатор участника ЭДО"
                      placeholder="2BM-…"
                      value={conf.fns_participant_id}
                      onChange={(v) => update("fns_participant_id", v)}
                    />
                    <Field
                      label="Версия УПД"
                      value={conf.upd_version}
                      onChange={(v) => update("upd_version", v)}
                    />
                    <Chip color={conf.xsd_loaded ? "success" : "warning"}>
                      {conf.xsd_loaded
                        ? "Схема XSD загружена"
                        : "Схема XSD не загружена"}
                    </Chip>
                  </Section>
                </div>
                <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                  {saveButton()}
                  <Button
                    variant="outlined"
                    disabled={busy}
                    startDecorator={<RefreshRounded />}
                    onClick={() =>
                      act(async () => {
                        const r = await request("configuration/probe/", {
                          method: "POST",
                          data: {},
                        });
                        setBoxes(r.organizations || []);
                        setConf(await request("configuration/"));
                        setNotice(
                          [r.message, r.warning, `Схема XSD: ${r.xsd_loaded ? "загружена" : "не получена"}`].filter(Boolean).join('. '),
                        );
                      })
                    }
                  >
                    Проверить сохранённый доступ
                  </Button>
                </Stack>
                {boxes.map((b) => (
                  <Section
                    key={b.box_id}
                    title={b.name}
                    description={`ИНН ${b.inn}`}
                  >
                    <Typography
                      sx={{ overflowWrap: "anywhere" }}
                      level="body-sm"
                    >
                      {b.box_id}
                    </Typography>
                    <Button
                      variant="soft"
                      onClick={() => {
                        update("box_id", b.box_id);
                        update(
                          "fns_participant_id",
                          b.fns_participant_id || "",
                        );
                        setNotice("Ящик выбран. Сохраните настройки.");
                      }}
                    >
                      Использовать этот ящик
                    </Button>
                  </Section>
                ))}
              </Stack>
            )}
            {tab === 2 && (
              <Stack gap={2}>
                <Section
                  title="Наша организация"
                  description="Это исполнитель по документам — наша компания или ИП. Его реквизиты отличаются от реквизитов заказчика в карточке клиента."
                >
                  <div className="doc-grid">
                    <Field
                      label="Наименование"
                      required
                      value={seller.name}
                      onChange={(v) => setSeller((s) => ({ ...s, name: v }))}
                    />
                    <Choice
                      label="Организационная форма"
                      value={seller.legal_form}
                      onChange={(v) =>
                        setSeller((s) => ({ ...s, legal_form: v }))
                      }
                      options={[
                        ["ip", "Индивидуальный предприниматель"],
                        ["ooo", "ООО"],
                        ["ip_npd", "ИП на НПД"],
                        ["self_employed", "Самозанятый"],
                      ]}
                    />
                    {Object.entries({
                      inn: "ИНН",
                      kpp: "КПП — для организации",
                      ogrn: "ОГРН / ОГРНИП",
                      legal_address: "Адрес для документов",
                      postal_address: "Почтовый адрес",
                    }).map(([k, label]) => (
                      <Field
                        key={k}
                        label={label}
                        value={seller[k]}
                        onChange={(v) => setSeller((s) => ({ ...s, [k]: v }))}
                      />
                    ))}
                  </div>
                  <CompanyLookup
                    form={sellerAsCompany(seller)}
                    setForm={updateCompany => setSeller(current => applySellerCompany(current, updateCompany))}
                    token={tokenEmployee}
                    ownerKey={`document-seller:${seller.id || 'new'}`}
                    disabled={busy}
                    onDirty={() => setNotice('Реквизиты заполнены. Проверьте данные и сохраните организацию.')}
                  />
                </Section>
                <div className="doc-settings-columns">
                  <Section
                    title="Банковские реквизиты нашей организации"
                    description="Счёт получателя оплаты. Заполнение банка заказчика не заменяет эти поля."
                  >
                    <div className="doc-grid">
                      {Object.entries({
                        bank_name: "Банк",
                        bank_bik: "БИК",
                        bank_account: "Расчётный счёт",
                        bank_corr_account: "Корреспондентский счёт",
                      }).map(([k, label]) => (
                        <Field
                          key={k}
                          label={label}
                          value={seller[k]}
                          onChange={(v) => setSeller((s) => ({ ...s, [k]: v }))}
                        />
                      ))}
                    </div>
                  </Section>
                  <Section title="Подписант">
                    {Object.entries({
                      director_name: "ФИО подписанта",
                      director_position: "Должность",
                      signatory_basis: "Основание полномочий",
                    }).map(([k, label]) => (
                      <Field
                        key={k}
                        label={label}
                        value={seller[k]}
                        onChange={(v) => setSeller((s) => ({ ...s, [k]: v }))}
                      />
                    ))}
                  </Section>
                </div>
                {seller.id && (
                  <Section
                    title="Печать и изображение подписи"
                    description="Изображения для печатных форм. Электронная подпись в Диадоке выполняется отдельно."
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                      {[
                        ["stamp_image", "Загрузить печать"],
                        ["signature_image", "Загрузить подпись"],
                      ].map(([key, label]) => (
                        <Button
                          key={key}
                          component="label"
                          variant="soft"
                          disabled={busy}
                        >
                          {label}
                          <input
                            type="file"
                            hidden
                            accept="image/png,image/jpeg"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                act(async () => {
                                  const data = new FormData();
                                  data.append(key, file);
                                  await request(
                                    `sellers/${seller.id}/images/`,
                                    { method: "POST", data },
                                  );
                                  setNotice("Изображение сохранено");
                                });
                              e.target.value = "";
                            }}
                          />
                        </Button>
                      ))}
                    </Stack>
                  </Section>
                )}
                <Stack direction="row">
                  <Button
                    loading={busy}
                    startDecorator={<SaveRounded />}
                    onClick={() =>
                      act(async () => {
                        setSeller(
                          await request(
                            seller.id ? `sellers/${seller.id}/` : "sellers/",
                            {
                              method: seller.id ? "PATCH" : "POST",
                              data: { ...seller, is_default: true },
                            },
                          ),
                        );
                        setNotice("Реквизиты сохранены");
                      })
                    }
                  >
                    Сохранить реквизиты
                  </Button>
                </Stack>
              </Stack>
            )}
            {tab === 3 && (
              <Stack gap={2}>
                <Section
                  title="Шаблоны печатных форм"
                  description="Заменяйте DOCX, сохраняя имена полей. Для PDF на сервере используется LibreOffice."
                >
                  <Choice
                    label="Тип документа"
                    value={tplType}
                    onChange={setTplType}
                    options={[["", "Все документы"], ...DOC_TYPES]}
                  />
                </Section>
                <div className="doc-grid">
                  {templates
                    .filter((t) => !tplType || t.doc_type === tplType)
                    .map((t) => (
                      <Section
                        key={t.id}
                        title={t.name}
                        description={`${typeName(t.doc_type)} · ${CLIENT_TYPES.find(([k]) => k === t.client_type)?.[1] || "Все типы клиентов"} · версия ${t.version}`}
                        action={<DescriptionRounded color="primary" />}
                      >
                        {(t.validation_issues || []).map((item, index) => <Alert key={`${item.code}:${index}`} color="warning">{item.message}</Alert>)}
                        <Toggle
                          title="Активен"
                          checked={t.is_active}
                          onChange={(v) =>
                            act(async () => {
                              await request(`templates/${t.id}/`, {
                                method: "PATCH",
                                data: { is_active: v },
                              });
                              setTemplates(await request("templates/"));
                            })
                          }
                        />
                        <Toggle
                          title="По умолчанию"
                          checked={t.is_default}
                          onChange={(v) =>
                            act(async () => {
                              await request(`templates/${t.id}/`, {
                                method: "PATCH",
                                data: { is_default: v },
                              });
                              setTemplates(await request("templates/"));
                            })
                          }
                        />
                        {t.has_file ? <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                          <Button variant="soft" startDecorator={<VisibilityRounded />} onClick={() => setPreviewTemplate(t)}>Просмотреть шаблон</Button>
                          <Button variant="outlined" disabled={busy} onClick={() => act(() => downloadTemplateFile(t.id, tokenEmployee, { isCurrent: () => pageScope.active && pageScopeRef.current === pageScope }))}>Скачать текущий DOCX</Button>
                        </Stack> : <Alert color="warning">Исходный DOCX не загружен. Добавьте файл шаблона.</Alert>}
                        <Button
                          component="label"
                          variant="outlined"
                          disabled={busy}
                        >
                          Заменить DOCX
                          <input
                            hidden
                            type="file"
                            accept=".docx"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                act(async () => {
                                  const data = new FormData();
                                  data.append("file", file);
                                  data.append("version", Number(t.version) + 1);
                                  await request(`templates/${t.id}/`, {
                                    method: "PATCH",
                                    data,
                                  });
                                  setTemplates(await request("templates/"));
                                  setNotice("Шаблон обновлён");
                                });
                              e.target.value = "";
                            }}
                          />
                        </Button>
                      </Section>
                    ))}
                </div>
                {!templates.length && (
                  <Alert color="warning">
                    Шаблоны пока не загружены. Выполните команду
                    setup_document_module на сервере.
                  </Alert>
                )}
              </Stack>
            )}
            {tab === 4 && (
              <Stack gap={2}>
                <Section title="Заказчики и правила выдачи" description="Реквизиты клиента и действующий договор. Наши банковские реквизиты находятся в отдельной вкладке «Наши реквизиты».">
                  <SearchPicker kind="clients" token={tokenEmployee} value={customer} onSelect={openClient} />
                </Section>
                {customer && <Section title={nameOf(customer)} description={customer.phone || customer.email}
                  action={<Button variant="soft" onClick={() => setClientEdit(value => !value)}>{clientEdit ? 'Скрыть реквизиты заказчика' : 'Редактировать реквизиты заказчика'}</Button>}>
                  {clientEdit && <CustomerEditor client={customer} token={tokenEmployee} onSaved={setCustomer} />}
                  <ClientDocumentProfileEditor key={`${customer.id}:${tokenEmployee}`} clientId={customer.id} token={tokenEmployee} />
                </Section>}
              </Stack>
            )}
            {tab === 5 && (
              <Stack gap={2}>
                <Section
                  title="Очередь документов"
                  description="Последние 100 заданий. После исправления реквизитов можно повторить обработку."
                >
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                    <Choice
                      label="Статус задания"
                      value={taskState}
                      onChange={setTaskState}
                      options={[["", "Все задания"], ...Object.entries(states)]}
                    />
                    <Button
                      variant="soft"
                      startDecorator={<RefreshRounded />}
                      disabled={busy}
                      onClick={() =>
                        act(async () => setTasks(await request("tasks/")))
                      }
                    >
                      Обновить очередь
                    </Button>
                  </Stack>
                </Section>
                {tasks
                  .filter((t) => !taskState || t.state === taskState)
                  .map((t) => (
                    <Section
                      key={t.id}
                      title={`${kinds[t.kind] || t.kind} · Событие №${t.event_id || "—"}`}
                      action={
                        <Chip
                          color={
                            t.state === "error"
                              ? "danger"
                              : t.state === "done"
                                ? "success"
                                : "neutral"
                          }
                        >
                          {states[t.state] || t.state}
                        </Chip>
                      }
                    >
                      <Typography level="body-sm">
                        Попыток: {t.attempts}
                      </Typography>
                      {t.error && (
                        <Alert color="danger" sx={{ overflowWrap: "anywhere" }}>
                          {t.error}
                        </Alert>
                      )}
                      {["error", "retry", "blocked", "cancelled"].includes(
                        t.state,
                      ) && (
                        <Button
                          variant="soft"
                          disabled={busy}
                          onClick={() =>
                            act(async () => {
                              await request(`tasks/${t.id}/retry/`, {
                                method: "POST",
                                data: {},
                              });
                              setTasks(await request("tasks/"));
                            })
                          }
                        >
                          Повторить после исправления
                        </Button>
                      )}
                    </Section>
                  ))}
                {!tasks.filter((t) => !taskState || t.state === taskState)
                  .length && (
                  <div className="doc-empty">
                    <Typography level="title-md">Заданий пока нет</Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      Здесь появятся результаты автоматического формирования и
                      передачи в ЭДО.
                    </Typography>
                  </div>
                )}
              </Stack>
            )}
          </Box>
        </>
      )}
      <TemplatePreviewModal template={previewTemplate} token={tokenEmployee} onClose={() => setPreviewTemplate(null)} />
    </Workspace>
  );
}
